import clientFile from '@fingerprinting/api/client/files'
import clientApplicationRequest from '@fingerprinting/api/client/application'

import type { TMetadataFile } from '@fingerprinting/types/schemas/file.schema'
import type { TApiResponseWithRequiredData } from '@/shared/schemas/api-schema'
import {
  keepPreviousData,
  type QueryKey,
  type UseQueryOptions,
  type UseMutationOptions,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import type {
  TClientListApplication,
  TClientApplicationDetail,
  TClientApplicationOverview,
  TClientApplicationListDocuments,
  TClientApplicationProfile,
} from '@fingerprinting/types/schemas/application.schema'
import type { IIntakeInternalDocumentsResponse } from '@fingerprinting/api/client/application'
import type { TClientDocumentStatusType } from '@fingerprinting/types'

export const APPLICATION_QUERY_KEY = {
  all: ['client', 'application'] as const,
  overview: (range?: { date?: string; startDate?: string; endDate?: string }) =>
    [
      ...APPLICATION_QUERY_KEY.all,
      'overview',
      range ?? {},
    ] as const,
  list: ({
    page,
    pageNumber,
    pageSize,
    ...filters
  }: Partial<IListApplicationFilterType> & Partial<TPageParams> & { pageNumber?: number }) =>
    [...APPLICATION_QUERY_KEY.all, 'list', filters, page ?? pageNumber, pageSize] as const,
  detail: (applicationId: string) =>
    [...APPLICATION_QUERY_KEY.all, 'detail', applicationId] as const,
  listDocument: (applicationId: string) =>
    [...APPLICATION_QUERY_KEY.detail(applicationId), 'documents'] as const,
  internalDocuments: (applicationId: string) =>
    [...APPLICATION_QUERY_KEY.detail(applicationId), 'internal-documents'] as const,
}

type TInfiniteApplicationPageParam = {
  page?: number
  pageSize?: number
}

export interface IApplicationOverviewQueryInput {
  date?: string
  startDate?: string
  endDate?: string
}

export const getClientApplicationOverviewOptions = (
  input?: string | IApplicationOverviewQueryInput
) => {
  const normalized: IApplicationOverviewQueryInput =
    typeof input === 'string' ? { date: input } : input ?? {}
  return {
    select: ({ data }) => data,
    queryKey: APPLICATION_QUERY_KEY.overview(normalized),
    queryFn: () => clientApplicationRequest.getApplicationOverview(normalized),
  } satisfies UseQueryOptions<
    TApiResponseWithRequiredData<TClientApplicationOverview>,
    Error,
    TClientApplicationOverview
  >
}

export const getInfinityListApplicationsOptions = ({ status }: { status: string }) => {
  return {
    initialPageParam: {
      page: 1,
    },
    select: ({ pages }) => pages.flatMap(({ data }) => data.items),
    getNextPageParam: ({ data }) => {
      const { pageNumber, totalCount, pageSize } = data
      const totalPages = Math.ceil(totalCount / pageSize)
      if (pageNumber >= totalPages) {
        return undefined
      }
      return { page: pageNumber + 1 }
    },
    queryKey: APPLICATION_QUERY_KEY.list({ status }),
    queryFn: ({ pageParam }) =>
      clientApplicationRequest.getListApplications({
        status,
        page: pageParam?.page ?? 1,
        pageSize: pageParam?.pageSize,
      }),
    refetchOnMount: 'always',
  } satisfies UseInfiniteQueryOptions<
    TApiResponseWithRequiredData<TClientListApplication>,
    Error,
    TClientApplicationDetail[],
    ReturnType<typeof APPLICATION_QUERY_KEY.list>,
    TInfiniteApplicationPageParam
  >
}

export interface IListApplicationFilterType {
  date: string
  status: string
  typeCode: string
  startDate?: string
  endDate?: string
}

export type IListApplicationQueryParams = IListApplicationFilterType &
  Partial<TPageParams> & {
    pageNumber?: number
  }

export const getListApplicationsOptions = (filters: IListApplicationQueryParams) => {
  return {
    select: ({ data }) => data,
    queryKey: APPLICATION_QUERY_KEY.list(filters),
    queryFn: () => clientApplicationRequest.getListApplications(filters),
    placeholderData: keepPreviousData,
  } satisfies UseQueryOptions<
    TApiResponseWithRequiredData<TClientListApplication>,
    Error,
    TClientListApplication
  >
}

export const getClientApplicationDetailOptions = (applicationId: string) => {
  return {
    select: ({ data }) => data,
    queryKey: APPLICATION_QUERY_KEY.detail(applicationId),
    queryFn: () => clientApplicationRequest.getApplicationDetail(applicationId),
    refetchOnMount: "always",
  } satisfies UseQueryOptions<
    TApiResponseWithRequiredData<TClientApplicationDetail>,
    Error,
    TClientApplicationDetail
  >
}

export const getClientApplicationListDocumentsOptions = (applicationId: string) => {
  return {
    select: ({ data }) => data,
    queryKey: APPLICATION_QUERY_KEY.listDocument(applicationId),
    queryFn: () => clientApplicationRequest.getListDocuments(applicationId),
    refetchOnMount: 'always',
  } satisfies UseQueryOptions<
    TApiResponseWithRequiredData<TClientApplicationListDocuments>,
    Error,
    TClientApplicationListDocuments
  >
}

export const getClientApplicationInternalDocumentsOptions = (applicationId: string) => {
  return {
    select: ({ data }) => data,
    queryKey: APPLICATION_QUERY_KEY.internalDocuments(applicationId),
    queryFn: async () => {
      const response = await clientApplicationRequest.getInternalDocuments(applicationId)
      if (!response.data) {
        throw new Error(response.message || 'Failed to fetch internal documents')
      }
      const normalizedData = Array.isArray(response.data)
        ? { documents: response.data, internalFiles: [] }
        : {
            documents: response.data.documents ?? [],
            internalFiles: response.data.internalFiles ?? [],
          }
      return {
        ...response,
        data: normalizedData,
      }
    },
    refetchOnMount: 'always',
  } satisfies UseQueryOptions<
    TApiResponseWithRequiredData<IIntakeInternalDocumentsResponse>,
    Error,
    IIntakeInternalDocumentsResponse
  >
}

interface IGetClientApplicationUploadDocumentMutateOptionsProps {
  applicationId: string
  applicationDocumentId: number
}

export const getClientApplicationUploadDocumentMutateOptions = ({
  applicationId,
  applicationDocumentId,
}: IGetClientApplicationUploadDocumentMutateOptionsProps) => {
  return {
    mutationFn: (files) =>
      clientApplicationRequest.uploadFiles(applicationId, applicationDocumentId, files),
  } satisfies UseMutationOptions<unknown, Error, TMetadataFile[]>
}

interface IGetClientApplicationDocumentPreviewFileOptionsProps<
  TQueryKey extends QueryKey = QueryKey,
> {
  src: string
  prefixQueryKey: TQueryKey
}

export const getClientApplicationDocumentPreviewFileOptions = <
  TQueryKey extends QueryKey = QueryKey,
>({
  src: srcProps,
  prefixQueryKey,
}: IGetClientApplicationDocumentPreviewFileOptionsProps<TQueryKey>) => {
  return {
    select: ({ blob, contentType }) => ({
      blob,
      contentType,
      url: URL.createObjectURL(blob),
    }),
    queryFn: () => clientFile.get(srcProps),
    queryKey: [...(Array.isArray(prefixQueryKey) ? prefixQueryKey : [prefixQueryKey]), srcProps],
  } satisfies UseQueryOptions<
    { blob: Blob; contentType: string },
    Error,
    { blob: Blob; url: string; contentType: string }
  >
}

interface IGetClientApplicationDocumentDeleteFileMutateOptionsProps {
  fileId: number
}

export const getClientApplicationDocumentDeleteFileMutateOptions = ({
  fileId,
}: IGetClientApplicationDocumentDeleteFileMutateOptionsProps) => {
  return {
    mutationFn: () => clientFile.delete(fileId),
  } satisfies UseMutationOptions<unknown, Error, void>
}

interface IGetClientApplicationDocumentUpdateFileStatusMutateOptionsProps {
  fileId: number
}

export const getClientApplicationDocumentUpdateFileStatusMutateOptions = ({
  fileId,
}: IGetClientApplicationDocumentUpdateFileStatusMutateOptionsProps) => {
  return {
    mutationFn: ({ status, rejectReason }: { status: TClientDocumentStatusType; rejectReason?: string }) =>
      clientFile.updateStatus(fileId, status, rejectReason),
  } satisfies UseMutationOptions<unknown, Error, { status: TClientDocumentStatusType; rejectReason?: string }>
}

interface IGetClientApplicationDocumentUpdateFileWithESignMutateOptionsProps {
  fileId: number
}

export const getClientApplicationDocumentUpdateFileWithESignMutateOptions = ({
  fileId,
}: IGetClientApplicationDocumentUpdateFileWithESignMutateOptionsProps) => {
  return {
    mutationFn: (signedFile: File) => clientFile.updateWithESign(fileId, signedFile),
  } satisfies UseMutationOptions<unknown, Error, File>
}

interface IGetClientApplicationSubmitSignedFileMutateOptionsProps {
  fileId: number
}

export const getClientApplicationSubmitSignedFileMutateOptions = ({
  fileId,
}: IGetClientApplicationSubmitSignedFileMutateOptionsProps) => {
  return {
    mutationFn: () => clientFile.submitSignedFile(fileId),
  } satisfies UseMutationOptions<unknown, Error, void>
}

interface IGetUpdateApplicantProfileMutateOptionsProps {
  applicationId: string
}

export const getUpdateApplicantProfileMutateOptions = ({
  applicationId,
}: IGetUpdateApplicantProfileMutateOptionsProps) => {
  return {
    mutationFn: (data: Partial<TClientApplicationProfile>) =>
      clientApplicationRequest.updateApplicantProfile(applicationId, data),
  } satisfies UseMutationOptions<
    TApiResponseWithRequiredData<TClientApplicationProfile>,
    Error,
    Partial<TClientApplicationProfile>
  >
}

export interface IUpdateApplicationPayload {
  clientId?: string
  name?: string
  typeCode?: string
  typeOther?: string | null
  reason?: string
  apostilleCountryCode?: string | null
  apostilleService?: boolean
  status?: number
  startDate?: string
  termOfAgreement?: boolean
  hearAboutUsFrom?: string
  trackingNumber?: string | null
  staffMessageContent?: string
  mailingDestination?: string
  mailingAddressOption?: string | null
  mailingCourierOption?: string | null
  mailingAddress?: {
    country: string
    unitApartment?: string | null
    street: string
    city: string
    stateProvince?: string | null
    zipPostalCode?: string | null
  } | null
}

interface IGetUpdateApplicationMutateOptionsProps {
  applicationId: string
}

export const getUpdateApplicationMutateOptions = ({
  applicationId,
}: IGetUpdateApplicationMutateOptionsProps) => {
  return {
    mutationFn: (data: IUpdateApplicationPayload) =>
      clientApplicationRequest.updateApplication(applicationId, data),
  } satisfies UseMutationOptions<
    TApiResponseWithRequiredData<TClientApplicationDetail>,
    Error,
    IUpdateApplicationPayload
  >
}

interface IGetUpdateApplicationNotesMutateOptionsProps {
  applicationId: string
}

export const getUpdateApplicationNotesMutateOptions = ({
  applicationId,
}: IGetUpdateApplicationNotesMutateOptionsProps) => {
  return {
    mutationFn: (notes: string) => clientApplicationRequest.updateApplicationNotes(applicationId, notes),
  } satisfies UseMutationOptions<unknown, Error, string>
}

interface IGetClientApplicationSubmitDocumentMutateOptionsProps {
  applicationId: string
  documentId: number
}

export const getClientApplicationSubmitDocumentMutateOptions = ({
  applicationId,
  documentId,
}: IGetClientApplicationSubmitDocumentMutateOptionsProps) => {
  return {
    mutationFn: () => clientApplicationRequest.submitDocument(applicationId, documentId),
  } satisfies UseMutationOptions<unknown, Error, void>
}
